<template>
  <div class="relative">
    <div ref="triggerEl" class="actions-menu-wrapper">
      <slot name="trigger">
        <button
          type="button"
          class="focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-2.5 py-0 text-center dark:bg-gray-100 dark:hover:bg-gray-700 dark:focus:ring-gray-800"
        >
          <EllipsisIcon
            class="tooltip-trigger"
            data-cy="tooltip-trigger"
            width="20"
          />
        </button>
      </slot>
    </div>
    <div
      id="actions-menu-click"
      ref="targetEl"
      data-popover
      role="tooltip"
      class="z-20 inline-block w-64 text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-lg shadow-sm dark:text-gray-400 dark:border-gray-600 dark:bg-gray-800"
    >
      <div class="px-3 py-2">
        <ul class="actions-menu-list text-sm !text-gray-700 dark:text-gray-200">
          <li
            v-for="action in visibleActions"
            :key="action.label"
            data-cy="actions-menu-action"
            :class="[
              `!text-gray-700 text-left px-2 flex items-center rounded-lg font-medium py-2 no-underline hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white action-item ${
                action.className || ''
              }`,
              { 'text-gray-300': action.disabled }
            ]"
            @click="performAction(action)"
          >
            <component
              :is="action.source"
              v-if="action.source"
              class="mr-2 image-icon"
            ></component>
            <i v-if="action.icon" :class="[action.icon, 'mr-2']"></i>
            <span v-if="action.label">{{ action.label }}</span>
            <span v-if="action.html" v-html="action.html"></span>
          </li>
        </ul>
      </div>
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Instance, Props } from "tippy.js";
import { computed, onMounted, ref } from "vue";

import EllipsisIcon from "@/images/dots-horizontal-outline.svg?component";
import "../../../vendor/assets/javascripts/tippy/themes/light.css";

const emit = defineEmits<{
  (e: string): void;
  (e: "on-show", instance: Instance): void;
  (e: "on-hide", instance: Instance): void;
}>();

interface Action {
  label: string;
  icon?: string;
  eventName: string;
  className?: string;
  visible?: boolean;
  disabled?: boolean;
  source?: string;
  html?: string;
}

const props = defineProps<{
  item?: any;
  actions: Action[];
  options?: Props;
  placement?: "top" | "bottom" | "left" | "right";
}>();

const targetEl = ref(null);
const triggerEl = ref(null);
const popover = ref<Instance | null>(null);

const onShow = (instance: Instance) => emit("on-show", instance);
const onHide = (instance: Instance) => emit("on-hide", instance);

const defaultOptions: Partial<Props> = {
  appendTo: document.body,
  interactive: true,
  delay: [300, 300],
  hideOnClick: true,
  arrow: false,
  placement: "left",
  trigger: "click",
  theme: "actions-menu light",
  onShow,
  onHide
};

const visibleActions = computed(() => {
  return props.actions.filter(
    (action) => !Object.keys(action).includes("visible") || action.visible
  );
});

onMounted(() => {
  // @ts-expect-error Missing global tippy ts declaration
  popover.value = tippy(triggerEl.value, {
    ...defaultOptions,
    content: targetEl.value,
    ...props.options
  } as Props);
});

const performAction = (action: Action) => {
  if (!action.disabled) {
    if (action.eventName) {
      popover.value?.hide();
      emit(action.eventName);
    }
  }
};
</script>

<style lang="scss" scoped>
ul {
  min-width: 100px;
  list-style-type: none;
  margin: 0;
  padding: 0;
  li {
    white-space: normal;
    cursor: pointer;
    &.disabled {
      cursor: not-allowed !important;
    }
    &.action-link-wrapper {
      position: relative;
      min-height: 36px;

      ::v-deep a.action-link {
        display: flex;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 8px 8px 8px 32px;
        color: inherit;
        text-decoration: none;
      }
    }
  }
}
.image-icon {
  width: 16px;
  height: 16px;
}

.tooltip-trigger {
  text-align: center;
  cursor: pointer;
}
</style>
